import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CmsserviceService } from 'src/app/common-cms-service/cmsservice.service';
import { ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare var $: any;

@Component({
    selector: 'app-sat-test',
    templateUrl: './sat-test.component.html',
    styleUrls: ['./sat-test.component.css'],
    standalone: false
})
export class SatTestComponent implements OnInit, OnDestroy {

  title = 'SAT Mock Test';

  // ---------------- FILTER MODEL ----------------
  filters: any = {
    categoryId: null,
    pattern: null,
    mode: null,
    month: null,
    center: null,
    search: ''
  };

  // ---------------- STATIC DATA ----------------
  months = [
    { label: 'January', value: 1 }, { label: 'February', value: 2 },
    { label: 'March', value: 3 }, { label: 'April', value: 4 },
    { label: 'May', value: 5 }, { label: 'June', value: 6 },
    { label: 'July', value: 7 }, { label: 'August', value: 8 },
    { label: 'September', value: 9 }, { label: 'October', value: 10 },
    { label: 'November', value: 11 }, { label: 'December', value: 12 }
  ];

  // ✅ REQUIRED BY HTML
  SATCategories = [
    { category_id: 36, category_name: 'SAT Full Mocks' },
    { category_id: 35, category_name: 'SAT Mini Mocks' }
  ];

  patterns = ['SAT', 'Math', 'Reading', 'Writing'];

  centers: string[] = [];

  // ---------------- DATA ----------------
  allTests: any[] = [];
  testList: any[] = [];

  private ngUnsubscribe = new Subject<void>();

  constructor(
    private cms: CmsserviceService,
    private route: ActivatedRoute,
    private titleService: Title,
    private ref: ChangeDetectorRef
  ) {}

  // ================= LIFECYCLE =================
  ngOnInit() {
    this.titleService.setTitle(this.title);

    // Preselect category via query param
    this.route.queryParams.subscribe(params => {
      if (params['category']) {
        this.filters.categoryId = params['category'];
      }
    });

    this.fetchAllSATTests();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ================= API =================
  fetchAllSATTests() {
    $('.loader-bg').fadeIn();

    const payload: any = {
      release: 'new',
      page: 1,
      user_id: +localStorage.getItem('userid') || 0,
      course: [],
      category: ['36', '35'], // 🔑 SAT Full + Mini
      type: [],
      exam_type: 'both',
      search: ''
    };

    this.allTests = [];
    this.fetchPage(payload);
  }

  fetchPage(payload: any) {
    this.cms.getTestList(payload)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {

        const chunk = res && res.data ? res.data : [];
        for (let i = 0; i < chunk.length; i++) {
          this.allTests.push(chunk[i]);
        }

        const extra = res && res.extra && res.extra.extraCount ? res.extra.extraCount : 0;

        if (extra > 0) {
          payload.page = payload.page + 1;
          this.fetchPage(payload);
          return;
        }

        this.centers = this.buildCenters(this.allTests);
        this.applyFilters();
        $('.loader-bg').fadeOut();
        this.ref.detectChanges();
      },
      () => $('.loader-bg').fadeOut());
  }

  // ================= FILTERING =================
  applyFilters() {
    let list = this.allTests.slice(0);

    // CATEGORY (by category_name – matches your HTML)
    if (this.filters.categoryId) {
      const selected = (this.filters.categoryId + '').toLowerCase();
      list = list.filter(t =>
        ((t.category || '') + '').toLowerCase().indexOf(selected) !== -1
      );
    }

    // PATTERN
    if (this.filters.pattern) {
      const p = this.filters.pattern.toLowerCase();
      list = list.filter(t =>
        ((t.test_name || '') + '').toLowerCase().indexOf(p) !== -1
      );
    }

    // MODE
    if (this.filters.mode) {
      const m = this.filters.mode.toLowerCase();
      list = list.filter(t =>
        ((t.exam_type || '') + '').toLowerCase().indexOf(m) !== -1
      );
    }

    // MONTH
    if (this.filters.month) {
      list = list.filter(t => this.testInMonth(t.test_date, this.filters.month));
    }

    // CENTER
    if (this.filters.center) {
      const c = this.filters.center.toLowerCase();
      list = list.filter(t =>
        ((t.location || t.all_location || '') + '').toLowerCase().indexOf(c) !== -1
      );
    }

    // SEARCH
    if (this.filters.search) {
      const s = this.filters.search.toLowerCase();
      list = list.filter(t =>
        JSON.stringify(t).toLowerCase().indexOf(s) !== -1
      );
    }

    this.testList = list;
    this.ref.detectChanges();
  }

  resetFilters() {
    this.filters = {
      categoryId: null,
      pattern: null,
      mode: null,
      month: null,
      center: null,
      search: ''
    };
    this.applyFilters();
  }

  // ================= HELPERS =================
  buildCenters(list: any[]): string[] {
    const set: any = {};
    for (let i = 0; i < list.length; i++) {
      const v = ((list[i].all_location || list[i].location || '') + '').trim();
      if (v) set[v] = true;
    }
    return Object.keys(set).sort();
  }

  testInMonth(dateStr: string, month: number): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr.replace(/(\d{2})\/(\d{2})\/(\d{4})/, '$2/$1/$3'));
    return !isNaN(d.getTime()) && (d.getMonth() + 1) === month;
  }

  trimChar(str: string, len: number = 30) {
    if (!str) return '';
    return str.length > len ? str.substr(0, len) + '…' : str;
  }

  getPatternFromName(name: string): string {
    const n = (name || '').toLowerCase();
    if (n.indexOf('math') !== -1) return 'Math';
    if (n.indexOf('read') !== -1) return 'Reading';
    if (n.indexOf('write') !== -1) return 'Writing';
    return 'SAT';
  }

  getPatternClass(name: string): string {
    return 'badge-pattern';
  }

  scrollToCalendar() {
    var el = document.getElementById('calendar-section');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }
}